import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  getRequestByToken, uploadReceipt, getAttachments,
  deleteAttachmentAndRevert, markTransactionLost,
  unmarkTransactionLost, saveClientNote, sendCompletionNotification,
  getReceiptSignedUrl,
} from '../lib/supabase'
import { formatDate, formatCurrency } from '../lib/csvParser'
import { useToast } from '../components/Toast'
import Logo from '../components/Logo'

// ─── Upload limits ────────────────────────────────────────────────────────────

const MAX_FILE_SIZE_MB = 10
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024
const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.png,.gif,.heic,.webp'
const ACCEPTED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'heic', 'webp']

function validateFile(file) {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `${file.name} is over ${MAX_FILE_SIZE_MB}MB`
  }
  const ext = file.name.split('.').pop()?.toLowerCase()
  if (!ext || !ACCEPTED_EXTENSIONS.includes(ext)) {
    return `${file.name} is not an accepted file type (PDF, JPG, PNG, GIF, HEIC, WEBP only)`
  }
  return null
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

// ─── Filename match scoring ──────────────────────────────────────────────────
// Returns true if the filename "looks like" it matches a transaction
// (contains the date or amount). Used to highlight likely target rows.

function filenameMatchesTransaction(filename, transaction) {
  if (!filename) return false
  const lower = filename.toLowerCase()

  // Match by amount — try the absolute value with and without decimals
  const absAmount = Math.abs(transaction.amount || 0)
  if (absAmount > 0) {
    const amountStr = absAmount.toFixed(2)
    const amountStrNoDecimal = Math.round(absAmount).toString()
    if (lower.includes(amountStr) || lower.includes(amountStrNoDecimal)) return true
  }

  // Match by date — try a few common formats
  if (transaction.date) {
    const d = new Date(transaction.date)
    if (!isNaN(d)) {
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      const formats = [
        `${yyyy}-${mm}-${dd}`,
        `${yyyy}${mm}${dd}`,
        `${dd}-${mm}-${yyyy}`,
        `${dd}${mm}${yyyy}`,
        `${dd}-${mm}-${String(yyyy).slice(2)}`,
      ]
      if (formats.some(f => lower.includes(f))) return true
    }
  }

  return false
}

// ─── Sortable column header ───────────────────────────────────────────────────

function SortTh({ label, field, sort, onSort, style = {} }) {
  const active = sort.field === field
  const next = active && sort.dir === 'asc' ? 'desc' : 'asc'
  return (
    <th
      onClick={() => onSort(field, next)}
      style={{ cursor: 'pointer', userSelect: 'none', ...style }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        <span style={{
          fontSize: '0.65rem',
          opacity: active ? 0.85 : 0.2,
          color: active ? 'var(--accent)' : 'inherit',
          transition: 'opacity 0.15s',
          lineHeight: 1,
        }}>
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </span>
    </th>
  )
}

function sortRows(rows, sort) {
  return [...rows].sort((a, b) => {
    let av, bv
    switch (sort.field) {
      case 'date':        av = a.date || ''; bv = b.date || ''; break
      case 'description': av = (a.description || '').toLowerCase(); bv = (b.description || '').toLowerCase(); break
      case 'type':        av = a.amount > 0 ? 0 : 1; bv = b.amount > 0 ? 0 : 1; break
      case 'amount':      av = Math.abs(a.amount || 0); bv = Math.abs(b.amount || 0); break
      default:            av = a.sort_order; bv = b.sort_order
    }
    if (av < bv) return sort.dir === 'asc' ? -1 : 1
    if (av > bv) return sort.dir === 'asc' ?  1 : -1
    return 0
  })
}

// ─── Lost receipt modal ───────────────────────────────────────────────────────

function LostReceiptModal({ transaction, token, onClose, onSaved }) {
  const [explanation, setExplanation] = useState(transaction.lost_explanation || '')
  const [whollyBusiness, setWhollyBusiness] = useState(
    transaction.lost_wholly_business !== null ? transaction.lost_wholly_business : true
  )
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!explanation.trim()) { toast('Please provide an explanation', 'error'); return }
    setSaving(true)
    const { error } = await markTransactionLost(transaction.id, explanation.trim(), whollyBusiness, token)
    setSaving(false)
    if (error) { toast(error.message, 'error'); return }
    onSaved()
    onClose()
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <div>
            <h3 style={{ marginBottom: 2 }}>Mark receipt as lost</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', margin: 0 }}>
              {transaction.description} · {formatCurrency(transaction.amount)}
            </p>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="explanation">What was this transaction for?</label>
              <textarea
                id="explanation"
                className="input"
                value={explanation}
                onChange={e => setExplanation(e.target.value)}
                placeholder="e.g. Business lunch with client at Café Rouge to discuss the Q3 project. Receipt not retained."
                rows={4}
                required
                autoFocus
                style={{ resize: 'vertical' }}
              />
              <p style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginTop: 6 }}>
                Please be specific — your bookkeeper may use this in place of the receipt.
              </p>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Was this expense wholly for business purposes?</label>
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                {[true, false].map(val => (
                  <label key={String(val)} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 16px', borderRadius: 'var(--radius)',
                    border: `1.5px solid ${whollyBusiness === val ? 'var(--accent)' : 'var(--surface-3)'}`,
                    background: whollyBusiness === val ? 'var(--accent-light)' : 'var(--white)',
                    cursor: 'pointer', flex: 1, justifyContent: 'center',
                    fontSize: '0.88rem', fontWeight: 500, transition: 'all 0.15s',
                  }}>
                    <input type="radio" name="wholly" value={String(val)}
                      checked={whollyBusiness === val}
                      onChange={() => setWhollyBusiness(val)}
                      style={{ display: 'none' }} />
                    {val ? '✓ Yes' : '✗ No — partially personal'}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Bulk upload zone ─────────────────────────────────────────────────────────
// Shown at the top of the page. Lets the client drop or pick multiple files;
// they go into a "staged" list (held in memory, not yet attached to a transaction).

function BulkUploadZone({ onFilesAdded }) {
  const fileRef = useRef()
  const cameraRef = useRef()
  const [dragOver, setDragOver] = useState(false)
  const toast = useToast()

  const handleFiles = (files) => {
    const accepted = []
    for (const file of files) {
      const err = validateFile(file)
      if (err) { toast(err, 'error'); continue }
      accepted.push(file)
    }
    if (accepted.length > 0) onFilesAdded(accepted)
  }

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(Array.from(e.dataTransfer.files)) }}
      style={{
        border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--surface-3)'}`,
        background: dragOver ? 'var(--accent-light)' : 'var(--white)',
        borderRadius: 'var(--radius-lg)',
        padding: '24px 20px',
        textAlign: 'center',
        transition: 'all 0.15s',
      }}
    >
      <input ref={fileRef} type="file" multiple hidden
        accept={ACCEPTED_TYPES}
        onChange={e => { handleFiles(Array.from(e.target.files)); e.target.value = '' }} />
      <input ref={cameraRef} type="file" hidden
        accept="image/*"
        capture="environment"
        onChange={e => { handleFiles(Array.from(e.target.files)); e.target.value = '' }} />

      <div style={{ fontSize: '1.6rem', marginBottom: 6 }}>📤</div>
      <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: 4 }}>
        Upload all your receipts at once
      </div>
      <div style={{ color: 'var(--ink-muted)', fontSize: '0.82rem', marginBottom: 14 }}>
        Drag and drop files here, or use the buttons below. You can attach them to transactions afterwards.
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => fileRef.current?.click()}
        >
          📁 Choose files
        </button>
        <button
          type="button"
          className="btn btn-secondary mobile-only"
          onClick={() => cameraRef.current?.click()}
        >
          📷 Take photo
        </button>
      </div>

      <div style={{ color: 'var(--ink-faint)', fontSize: '0.75rem', marginTop: 14 }}>
        PDF, JPG, PNG, HEIC, WEBP · up to {MAX_FILE_SIZE_MB}MB each
      </div>
    </div>
  )
}

// ─── Staged files strip ──────────────────────────────────────────────────────
// Shows files that have been bulk-uploaded but not yet assigned to a
// transaction. Each file can be: dragged to a row (desktop), tapped to "select"
// then tap a row to assign (mobile/touch), or removed.

function StagedFiles({ files, selectedId, onSelect, onRemove, onDragStart, onDragEnd }) {
  if (files.length === 0) return null

  return (
    <div className="card fade-up-1" style={{ padding: '14px 18px', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
        <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>
          {files.length} file{files.length !== 1 ? 's' : ''} ready to attach
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)' }}>
          {selectedId
            ? '👇 Tap a transaction below to attach this file'
            : 'Drag onto a row, or tap a file then tap a row'}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {files.map(f => {
          const isSelected = selectedId === f.id
          return (
            <div
              key={f.id}
              draggable
              onDragStart={e => { onDragStart(f.id); e.dataTransfer.effectAllowed = 'move' }}
              onDragEnd={onDragEnd}
              onClick={() => onSelect(f.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', borderRadius: 'var(--radius)',
                background: isSelected ? 'var(--accent-light)' : 'var(--surface)',
                border: `2px solid ${isSelected ? 'var(--accent)' : 'var(--surface-3)'}`,
                cursor: 'pointer', userSelect: 'none',
                fontSize: '0.82rem',
                transition: 'all 0.12s',
              }}
              title={f.file.name}
            >
              <span style={{ fontSize: '1rem' }}>📎</span>
              <span style={{
                fontWeight: 500, maxWidth: 220,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {f.file.name}
              </span>
              <span style={{ color: 'var(--ink-faint)', fontSize: '0.74rem', whiteSpace: 'nowrap' }}>
                {formatFileSize(f.file.size)}
              </span>
              <button
                onClick={e => { e.stopPropagation(); onRemove(f.id) }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--ink-faint)', fontSize: '0.95rem',
                  padding: 0, lineHeight: 1, marginLeft: 2,
                }}
                title="Remove"
              >✕</button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Upload cell ──────────────────────────────────────────────────────────────

function UploadCell({ transaction, token, onRefresh, refreshKey }) {
  const fileRef = useRef()
  const cameraRef = useRef()
  const [uploading, setUploading] = useState(false)
  const [attachments, setAttachments] = useState([])
  const [showLost, setShowLost] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const toast = useToast()

  const loadAttachments = useCallback(async () => {
    const { data } = await getAttachments(transaction.id, token)
    const list = data || []

    // Sign every attachment URL in parallel. The signed URL is what the
    // <a href={...}> below uses, so we set it on each attachment object
    // alongside the row data. If signing fails for one file, we leave
    // signedUrl null and the UI shows the filename with a broken link
    // (better than removing the file from view; the client can re-load).
    const signed = await Promise.all(
      list.map(async (a) => {
        const { signedUrl } = await getReceiptSignedUrl(a.file_path, token)
        return { ...a, signedUrl }
      })
    )
    setAttachments(signed)
  }, [transaction.id, token])

  // Reload whenever the parent signals a refresh (parent increments refreshKey
  // after every load() so child attachments stay in sync without manual wiring).
  useEffect(() => { loadAttachments() }, [loadAttachments, refreshKey])

  const handleFiles = async (files) => {
    for (const file of files) {
      const err = validateFile(file)
      if (err) { toast(err, 'error'); continue }
      setUploading(true)
      const { error } = await uploadReceipt(transaction.id, token, file)
      setUploading(false)
      if (error) toast('Upload failed: ' + error.message, 'error')
      else { toast('Uploaded ✓', 'success'); await loadAttachments(); onRefresh() }
    }
  }

  const handleDeleteFile = async (a) => {
    if (!window.confirm(`Remove "${a.file_name}"?`)) return
    const { error } = await deleteAttachmentAndRevert(a.id, a.file_path, transaction.id, token)
    if (error) toast(error.message, 'error')
    else { await loadAttachments(); onRefresh() }
  }

  const handleUnmarkLost = async () => {
    const { error } = await unmarkTransactionLost(transaction.id, token)
    if (error) toast(error.message, 'error')
    else onRefresh()
  }

  if (transaction.status === 'lost') {
    return (
      <div style={{ fontSize: '0.82rem' }}>
        <div style={{ color: 'var(--amber)', fontWeight: 600, marginBottom: 2 }}>⚠ Receipt lost</div>
        <div style={{ color: 'var(--ink-muted)', marginBottom: 4, fontSize: '0.78rem' }}>
          {transaction.lost_explanation}
          {transaction.lost_wholly_business !== null && (
            <span style={{ marginLeft: 6 }}>· Wholly business: {transaction.lost_wholly_business ? 'Yes' : 'No'}</span>
          )}
        </div>
        <button
          onClick={e => { e.stopPropagation(); handleUnmarkLost() }}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink-faint)', fontSize: '0.76rem', textDecoration: 'underline' }}
        >
          Undo
        </button>
      </div>
    )
  }

  return (
    <>
      {attachments.length > 0 && (
        <div style={{ marginBottom: 6, display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {attachments.map(a => (
            <div key={a.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '2px 8px', borderRadius: 6,
              background: 'var(--green-light)', border: '1px solid rgba(34,160,107,0.2)',
              fontSize: '0.75rem',
            }}>
              <a href={a.signedUrl || '#'} target="_blank" rel="noopener noreferrer"
                onClick={e => { e.stopPropagation(); if (!a.signedUrl) e.preventDefault() }}
                style={{ color: 'var(--green)', fontWeight: 500, textDecoration: 'none' }}>
                📎 {a.file_name}
              </a>
              <button
                onClick={e => { e.stopPropagation(); handleDeleteFile(a) }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-faint)', fontSize: '0.8rem', padding: 0, lineHeight: 1 }}
              >✕</button>
            </div>
          ))}
        </div>
      )}

      <div
        onClick={e => { e.stopPropagation(); fileRef.current?.click() }}
        onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); e.stopPropagation(); setDragOver(false); handleFiles(Array.from(e.dataTransfer.files)) }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '5px 12px', borderRadius: 'var(--radius)',
          border: `1.5px dashed ${dragOver ? 'var(--accent)' : attachments.length ? 'rgba(34,160,107,0.4)' : 'var(--surface-3)'}`,
          background: dragOver ? 'var(--accent-light)' : attachments.length ? 'var(--green-light)' : 'var(--surface)',
          cursor: 'pointer', fontSize: '0.8rem',
          color: attachments.length ? 'var(--green)' : 'var(--ink-muted)',
          transition: 'all 0.15s',
        }}
      >
        <input ref={fileRef} type="file" multiple hidden
          accept={ACCEPTED_TYPES}
          onChange={e => { handleFiles(Array.from(e.target.files)); e.target.value = '' }} />
        <input ref={cameraRef} type="file" hidden
          accept="image/*"
          capture="environment"
          onChange={e => { handleFiles(Array.from(e.target.files)); e.target.value = '' }} />
        {uploading
          ? <><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Uploading…</>
          : <>{attachments.length ? '＋ Add file' : '📤 Upload receipt'}</>
        }
      </div>
      <button
        type="button"
        onClick={e => { e.stopPropagation(); cameraRef.current?.click() }}
        className="mobile-only"
        style={{
          alignItems: 'center', gap: 4,
          padding: '5px 10px', borderRadius: 'var(--radius)',
          border: '1.5px solid var(--surface-3)', background: 'var(--white)',
          cursor: 'pointer', fontSize: '0.78rem',
          color: 'var(--ink-muted)', marginLeft: 6,
        }}
        title="Take photo"
      >
        📷
      </button>

      {!attachments.length && (
        <button
          onClick={e => { e.stopPropagation(); setShowLost(true) }}
          style={{ display: 'block', marginTop: 5, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink-faint)', fontSize: '0.75rem', textDecoration: 'underline', textAlign: 'left' }}
        >
          Can't find it? Mark as lost
        </button>
      )}

      {showLost && (
        <LostReceiptModal
          transaction={transaction}
          token={token}
          onClose={() => setShowLost(false)}
          onSaved={onRefresh}
        />
      )}
    </>
  )
}

// ─── Note cell ────────────────────────────────────────────────────────────────

function NoteCell({ transaction, token, onRefresh }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(transaction.client_note || '')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    await saveClientNote(transaction.id, value.trim(), token)
    setSaving(false)
    setEditing(false)
    onRefresh()
  }

  if (editing) {
    return (
      <div onClick={e => e.stopPropagation()}>
        <textarea
          className="input"
          value={value}
          onChange={e => setValue(e.target.value)}
          rows={2}
          autoFocus
          placeholder="Add a note for your bookkeeper…"
          style={{ fontSize: '0.8rem', resize: 'vertical', minWidth: 180 }}
        />
        <div style={{ display: 'flex', gap: 6, marginTop: 5 }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}
            style={{ fontSize: '0.75rem', padding: '4px 10px' }}>
            {saving ? '…' : 'Save'}
          </button>
          <button className="btn btn-ghost btn-sm"
            onClick={() => { setEditing(false); setValue(transaction.client_note || '') }}
            style={{ fontSize: '0.75rem', padding: '4px 10px' }}>
            Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ fontSize: '0.8rem' }} onClick={e => e.stopPropagation()}>
      {value && <div style={{ color: 'var(--blue)', marginBottom: 4 }}>💬 {value}</div>}
      <button
        onClick={() => setEditing(true)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink-faint)', fontSize: '0.75rem', textDecoration: 'underline' }}
      >
        {value ? 'Edit note' : '+ Add note'}
      </button>
    </div>
  )
}

// ─── Main portal ──────────────────────────────────────────────────────────────

export default function ClientPortal() {
  const { token } = useParams()
  const toast = useToast()
  const [request, setRequest] = useState(null)
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [sort, setSort] = useState({ field: 'sort_order', dir: 'asc' })

  // Bulk-uploaded files held in memory until assigned to a transaction.
  // Each entry: { id (local uuid), file (File object) }
  const [stagedFiles, setStagedFiles] = useState([])
  const [selectedStagedId, setSelectedStagedId] = useState(null)
  const [draggingStagedId, setDraggingStagedId] = useState(null)
  // Track which transaction is currently receiving an assigned upload
  const [assigningToTxId, setAssigningToTxId] = useState(null)

  // Increments after every load() — used to signal child UploadCells to
  // reload their attachment lists without each one needing its own wiring.
  const [refreshKey, setRefreshKey] = useState(0)

  // Filter toggle — hide rows that are uploaded or marked lost
  const [hideCompleted, setHideCompleted] = useState(false)

  // Track whether we've already sent the completion notification this session
  const notificationSentRef = useRef(false)

  const load = useCallback(async () => {
    const { data, error } = await getRequestByToken(token)
    if (error || !data) { setNotFound(true); setLoading(false); return }
    setRequest(data)
    const sorted = [...(data.transactions || [])].sort((a, b) => a.sort_order - b.sort_order)
    setTransactions(sorted)
    setLoading(false)
    setRefreshKey(k => k + 1)

    // Check if request is now fully resolved and notify bookkeeper if so
    const txs = data.transactions || []
    const total = txs.length
    const pending = txs.filter(t => t.status === 'pending').length

    if (total > 0 && pending === 0 && !notificationSentRef.current && data.bookkeeper_notify !== false) {
      notificationSentRef.current = true
      const uploaded = txs.filter(t => t.status === 'uploaded').length
      const lost     = txs.filter(t => t.status === 'lost').length
      const requestLink = `${window.location.origin}/requests/${data.id}`

      sendCompletionNotification({
        bookkeeperEmail: data.bookkeeper_email,
        bookkeeperName:  data.bookkeeper_display_name,
        clientName:      data.clients?.name || 'Your client',
        requestTitle:    data.title,
        requestLink,
        uploadedCount:   uploaded,
        lostCount:       lost,
      }).catch(() => {})
    }
  }, [token])

  useEffect(() => { load() }, [load])

  // Warn the user before they leave the page if they have unassigned files
  useEffect(() => {
    const handler = (e) => {
      if (stagedFiles.length > 0) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [stagedFiles.length])

  // Auto-scroll the page when dragging a staged file near the viewport edges.
  // Browsers don't do this natively during HTML5 drag operations, which makes
  // it impossible to drop onto rows that are off-screen.
  useEffect(() => {
    if (!draggingStagedId) return

    const SCROLL_ZONE = 100   // px from edge that triggers scroll
    const SCROLL_SPEED = 18   // px per tick
    let rafId = null
    let lastY = 0

    const tick = () => {
      const vh = window.innerHeight
      if (lastY > 0 && lastY < SCROLL_ZONE) {
        window.scrollBy(0, -SCROLL_SPEED)
      } else if (lastY > vh - SCROLL_ZONE && lastY < vh) {
        window.scrollBy(0, SCROLL_SPEED)
      }
      rafId = requestAnimationFrame(tick)
    }

    const onDragOver = (e) => { lastY = e.clientY }

    document.addEventListener('dragover', onDragOver)
    rafId = requestAnimationFrame(tick)

    return () => {
      document.removeEventListener('dragover', onDragOver)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [draggingStagedId])

  const handleSort = (field, dir) => setSort({ field, dir })

  // ─── Staging handlers ────────────────────────────────────────────────────
  const handleStageFiles = (files) => {
    const newEntries = files.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
    }))
    setStagedFiles(prev => [...prev, ...newEntries])
  }

  const handleRemoveStaged = (id) => {
    setStagedFiles(prev => prev.filter(f => f.id !== id))
    if (selectedStagedId === id) setSelectedStagedId(null)
  }

  const handleSelectStaged = (id) => {
    setSelectedStagedId(prev => prev === id ? null : id)
  }

  // Assign a staged file to a transaction (uploads it, then removes from staging)
  const assignStagedFile = async (stagedId, transactionId) => {
    const entry = stagedFiles.find(f => f.id === stagedId)
    if (!entry) return

    setAssigningToTxId(transactionId)
    const { error } = await uploadReceipt(transactionId, token, entry.file)
    setAssigningToTxId(null)

    if (error) {
      toast('Upload failed: ' + error.message, 'error')
      return
    }
    toast(`Attached ${entry.file.name} ✓`, 'success')
    setStagedFiles(prev => prev.filter(f => f.id !== stagedId))
    if (selectedStagedId === stagedId) setSelectedStagedId(null)
    await load()
  }

  // Tapped a row while a staged file is selected
  const handleRowTap = (transactionId) => {
    if (selectedStagedId) {
      assignStagedFile(selectedStagedId, transactionId)
    }
  }

  // Dropped a staged file onto a row
  const handleRowDrop = (e, transactionId) => {
    e.preventDefault()
    if (draggingStagedId) {
      assignStagedFile(draggingStagedId, transactionId)
    }
  }

  // ─── Filename used to highlight likely matching rows ─────────────────────
  const activeFilenameForMatching = useMemo(() => {
    const id = selectedStagedId || draggingStagedId
    if (!id) return null
    return stagedFiles.find(f => f.id === id)?.file.name || null
  }, [selectedStagedId, draggingStagedId, stagedFiles])

  if (loading) return (
    <div className="loading-screen">
      <div style={{ textAlign: 'center' }}>
        <Logo variant="icon" size={48} style={{ margin: '0 auto 16px' }} />
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    </div>
  )

  if (notFound) return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 16 }}>🔍</div>
        <h2>Link not found</h2>
        <p style={{ color: 'var(--ink-muted)', marginTop: 8 }}>This request link may have expired or is invalid.</p>
      </div>
    </div>
  )

  // Retention-state landing page. Shown when the request has crossed
  // the 7-year retention boundary -- either soft-deleted (UI hidden,
  // files still on disk for 30 days) or hard-deleted (files purged).
  // From the client's perspective, both states look the same: read-only,
  // no uploads. Checked first because it's the terminal state; even an
  // archived client whose request also hits 7-year retention should see
  // this page.
  if (request?.deletion_scheduled_at || request?.deleted_at) {
    const practiceLabel = request.bookkeeper_practice_name
                       || request.bookkeeper_display_name
                       || 'your bookkeeper'
    return (
      <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{
          background: 'var(--white)',
          borderRadius: 12,
          padding: '40px 32px',
          maxWidth: 520,
          textAlign: 'center',
          boxShadow: 'var(--shadow-sm)',
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>ℹ️</div>
          <h2 style={{ marginBottom: 12, fontSize: '1.4rem' }}>
            This receipt request is no longer available
          </h2>
          <p style={{
            color: 'var(--ink-muted)',
            lineHeight: 1.6,
            fontSize: '0.95rem',
            marginBottom: 8,
          }}>
            This request is from more than seven years ago and is no
            longer active. If you need access to historical records,
            please contact {practiceLabel} directly.
          </p>
        </div>
      </div>
    )
  }

  // Archived-client landing page. Shown when the bookkeeper has archived
  // the client. Same shape as the cancelled-request page (read-only, no
  // uploads) but with wording tied to the client account rather than the
  // request. Takes precedence over the cancelled check below since archive
  // is the broader state (and an archived client probably has cancelled
  // requests too if the bookkeeper picked the cascade option).
  if (request?.clients?.archived) {
    const practiceLabel = request.bookkeeper_practice_name
                       || request.bookkeeper_display_name
                       || 'your bookkeeper'
    return (
      <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{
          background: 'var(--white)',
          borderRadius: 12,
          padding: '40px 32px',
          maxWidth: 520,
          textAlign: 'center',
          boxShadow: 'var(--shadow-sm)',
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>ℹ️</div>
          <h2 style={{ marginBottom: 12, fontSize: '1.4rem' }}>
            This receipt request is no longer active
          </h2>
          <p style={{
            color: 'var(--ink-muted)',
            lineHeight: 1.6,
            fontSize: '0.95rem',
            marginBottom: 8,
          }}>
            {practiceLabel} is no longer accepting uploads for this account,
            so there's nothing to do here.
          </p>
          <p style={{
            color: 'var(--ink-muted)',
            lineHeight: 1.6,
            fontSize: '0.9rem',
            marginTop: 16,
          }}>
            Any receipts you'd already uploaded are safe and still on file.
            If this is a mistake, please contact {practiceLabel} directly.
          </p>
        </div>
      </div>
    )
  }

  // Cancelled-state landing page. Shown when the bookkeeper has cancelled
  // the request. Read-only: no uploads, no transaction list, no progress.
  // The bookkeeper's practice name is included if known so the client
  // recognises who cancelled it. Anything they've already uploaded is
  // safe; this just stops them from doing anything new.
  if (request?.cancelled_at) {
    const practiceLabel = request.bookkeeper_practice_name
                       || request.bookkeeper_display_name
                       || 'your bookkeeper'
    return (
      <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{
          background: 'var(--white)',
          borderRadius: 12,
          padding: '40px 32px',
          maxWidth: 520,
          textAlign: 'center',
          boxShadow: 'var(--shadow-sm)',
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>ℹ️</div>
          <h2 style={{ marginBottom: 12, fontSize: '1.4rem' }}>
            This request has been cancelled
          </h2>
          <p style={{
            color: 'var(--ink-muted)',
            lineHeight: 1.6,
            fontSize: '0.95rem',
            marginBottom: 8,
          }}>
            {practiceLabel} has cancelled this receipt request, so no
            further uploads are needed.
          </p>
          <p style={{
            color: 'var(--ink-muted)',
            lineHeight: 1.6,
            fontSize: '0.9rem',
            marginTop: 16,
          }}>
            Any receipts you'd already uploaded are safe and still on file.
            If this is a mistake, please contact {practiceLabel} directly.
          </p>
        </div>
      </div>
    )
  }

  const sortedTxs = sortRows(transactions, sort)
  const visibleTxs = hideCompleted
    ? sortedTxs.filter(t => t.status !== 'uploaded' && t.status !== 'lost')
    : sortedTxs
  const total = transactions.length
  const uploaded = transactions.filter(t => t.status === 'uploaded').length
  const lost = transactions.filter(t => t.status === 'lost').length
  const resolved = uploaded + lost
  const allDone = resolved === total && total > 0

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)' }}>
      <div style={{ background: 'var(--white)', borderBottom: '1.5px solid var(--surface-2)' }}>
        <div className="container" style={{ padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Logo variant="icon" size={32} />
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', color: 'var(--ink)' }}>Receipt Chaser</span>
          </div>
          {(request.bookkeeper_practice_name || request.bookkeeper_display_name) && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--ink-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                Request from
              </div>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--ink)' }}>
                {request.bookkeeper_practice_name || request.bookkeeper_display_name}
              </div>
            </div>
          )}
        </div>
      </div>

      <main className="container" style={{ padding: '32px 24px', maxWidth: 1100 }}>
        <div className="fade-up" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 6 }}>
            <h1 style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', margin: 0 }}>{request.title}</h1>
            <a
              href="/faq#clients"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 'var(--radius)',
                background: 'var(--accent-light)', color: 'var(--accent)',
                fontSize: '0.85rem', fontWeight: 700, textDecoration: 'none',
                whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              ❓ Need help?
            </a>
          </div>
          <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem', lineHeight: 1.6 }}>
            Upload all your receipts at the top, then drag or tap each one onto the matching transaction.
            If you can't find one, use <strong>Mark as lost</strong> and describe the purchase.
          </p>
        </div>

        {request.bookkeeper_notes && (
          <div className="card fade-up-1" style={{
            padding: '14px 20px', marginBottom: 20,
            borderLeft: '4px solid var(--accent)', background: 'var(--accent-light)',
          }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--accent)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Note from your bookkeeper
            </div>
            <p style={{ fontSize: '0.88rem', color: 'var(--ink)', margin: 0, lineHeight: 1.6 }}>
              {request.bookkeeper_notes}
            </p>
          </div>
        )}

        {/* Bulk upload zone */}
        <div className="fade-up-1" style={{ marginBottom: stagedFiles.length > 0 ? 16 : 24 }}>
          <BulkUploadZone onFilesAdded={handleStageFiles} />
        </div>

        {/* Staged files strip */}
        <StagedFiles
          files={stagedFiles}
          selectedId={selectedStagedId}
          onSelect={handleSelectStaged}
          onRemove={handleRemoveStaged}
          onDragStart={setDraggingStagedId}
          onDragEnd={() => setDraggingStagedId(null)}
        />

        <div className="card fade-up-1" style={{ padding: '16px 20px', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: '0.88rem', fontWeight: 500, color: allDone ? 'var(--green)' : 'var(--ink)' }}>
              {allDone ? '🎉 All done — thank you!' : `${resolved} of ${total} completed`}
            </span>
            <div style={{ display: 'flex', gap: 14, fontSize: '0.78rem', color: 'var(--ink-muted)' }}>
              {uploaded > 0 && <span style={{ color: 'var(--green)' }}>✓ {uploaded} uploaded</span>}
              {lost > 0 && <span style={{ color: 'var(--amber)' }}>⚠ {lost} marked lost</span>}
              {(total - resolved) > 0 && <span>⏳ {total - resolved} to do</span>}
            </div>
          </div>
          <div style={{ height: 6, background: 'var(--surface-2)', borderRadius: 100, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${total ? (resolved / total) * 100 : 0}%`,
              background: allDone ? 'var(--green)' : 'var(--accent)',
              borderRadius: 100, transition: 'width 0.5s ease',
            }} />
          </div>

          {resolved > 0 && !allDone && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontSize: '0.82rem', color: 'var(--ink-muted)',
                cursor: 'pointer', userSelect: 'none',
              }}>
                <input
                  type="checkbox"
                  checked={hideCompleted}
                  onChange={e => setHideCompleted(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Hide completed ({resolved})
              </label>
            </div>
          )}
        </div>

        <div className="card fade-up-2 hide-mobile">
          <div className="table-wrap">
            <table style={{ tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      #
                    </span>
                  </th>
                  <SortTh label="Date"        field="date"        sort={sort} onSort={handleSort} style={{ width: 100 }} />
                  <SortTh label="Description" field="description" sort={sort} onSort={handleSort} />
                  <SortTh label="Type"        field="type"        sort={sort} onSort={handleSort} style={{ width: 110 }} />
                  <SortTh label="Amount"      field="amount"      sort={sort} onSort={handleSort} style={{ width: 100, textAlign: 'right' }} />
                  <th style={{ width: 200 }}>Receipt</th>
                  <th style={{ width: 160 }}>Note</th>
                </tr>
              </thead>
              <tbody>
                {visibleTxs.map((tx, i) => {
                  const isDone = tx.status === 'uploaded' || tx.status === 'lost'
                  const isAssignTarget = !!(activeFilenameForMatching && tx.status !== 'lost')
                  const isLikelyMatch = isAssignTarget && filenameMatchesTransaction(activeFilenameForMatching, tx)
                  const isAssigning = assigningToTxId === tx.id

                  // Row receives drag/tap actions only when a staged file is active
                  // and the row is a valid target (not 'lost')
                  const rowDropHandlers = isAssignTarget ? {
                    onDragOver: e => e.preventDefault(),
                    onDrop:     e => handleRowDrop(e, tx.id),
                    onClick:    () => handleRowTap(tx.id),
                  } : {}

                  // Background priority: in-flight upload > likely match > status colour
                  const rowBackground = isAssigning
                    ? 'var(--accent-light)'
                    : isLikelyMatch
                      ? 'rgba(232,85,42,0.06)'
                      : tx.status === 'uploaded'
                        ? 'rgba(34,160,107,0.08)'
                        : tx.status === 'lost'
                          ? 'rgba(217,119,6,0.06)'
                          : 'transparent'

                  return (
                    <tr
                      key={tx.id}
                      {...rowDropHandlers}
                      style={{
                        opacity: isDone ? 0.92 : 1,
                        cursor: (selectedStagedId && isAssignTarget) ? 'pointer' : 'default',
                        background: rowBackground,
                        outline: isLikelyMatch ? '2px solid var(--accent)' : 'none',
                        outlineOffset: -2,
                        transition: 'background 0.15s, outline 0.15s',
                      }}
                    >
                      <td style={{ verticalAlign: 'top', paddingTop: 14 }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%',
                          background: tx.status === 'uploaded' ? 'var(--green-light)'
                            : tx.status === 'lost' ? 'var(--amber-light)' : 'var(--surface-2)',
                          color: tx.status === 'uploaded' ? 'var(--green)'
                            : tx.status === 'lost' ? 'var(--amber)' : 'var(--ink-faint)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.65rem', fontWeight: 700,
                        }}>
                          {tx.status === 'uploaded' ? '✓' : tx.status === 'lost' ? '!' : i + 1}
                        </div>
                      </td>
                      <td style={{ color: 'var(--ink-muted)', fontSize: '0.82rem', whiteSpace: 'nowrap', verticalAlign: 'top', paddingTop: 14 }}>
                        {formatDate(tx.date)}
                      </td>
                      <td style={{ verticalAlign: 'top', paddingTop: 14 }}>
                        <span style={{ fontSize: '0.88rem', fontWeight: 500, lineHeight: 1.4 }}>{tx.description}</span>
                        {isLikelyMatch && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--accent)', marginTop: 4, fontWeight: 600 }}>
                            ↳ likely match
                          </div>
                        )}
                      </td>
                      <td style={{ verticalAlign: 'top', paddingTop: 16 }}>
                        <span style={{
                          fontSize: '0.72rem', fontWeight: 600, padding: '2px 7px', borderRadius: 100,
                          background: tx.amount > 0 ? 'var(--green-light)' : 'var(--surface-2)',
                          color: tx.amount > 0 ? 'var(--green)' : 'var(--ink-muted)',
                          whiteSpace: 'nowrap',
                        }}>
                          {tx.amount > 0 ? 'Income' : 'Expenditure'}
                        </span>
                      </td>
                      <td style={{
                        textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap',
                        verticalAlign: 'top', paddingTop: 14, fontSize: '0.9rem',
                        color: tx.amount > 0 ? 'var(--green)' : 'var(--ink)',
                      }}>
                        {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount)}
                      </td>
                      <td style={{ verticalAlign: 'top', paddingTop: 12 }}>
                        <UploadCell transaction={tx} token={token} onRefresh={load} refreshKey={refreshKey} />
                      </td>
                      <td style={{ verticalAlign: 'top', paddingTop: 12 }}>
                        <NoteCell transaction={tx} token={token} onRefresh={load} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Mobile card list ───────────────────────────────────────────── */}
        <div className="mobile-only-block">
          {/* Sort dropdown */}
          <div className="card" style={{ padding: '12px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', fontWeight: 500 }}>Sort by</span>
            <select
              className="input"
              value={`${sort.field}:${sort.dir}`}
              onChange={e => {
                const [field, dir] = e.target.value.split(':')
                setSort({ field, dir })
              }}
              style={{ flex: 1, maxWidth: 220, padding: '6px 10px', fontSize: '0.85rem', height: 'auto' }}
            >
              <option value="sort_order:asc">Default order</option>
              <option value="date:asc">Date (oldest first)</option>
              <option value="date:desc">Date (newest first)</option>
              <option value="amount:asc">Amount (low to high)</option>
              <option value="amount:desc">Amount (high to low)</option>
              <option value="description:asc">Description (A–Z)</option>
              <option value="description:desc">Description (Z–A)</option>
            </select>
          </div>

          {/* Cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {visibleTxs.map((tx, i) => {
              const isAssignTarget = !!(activeFilenameForMatching && tx.status !== 'lost')
              const isLikelyMatch = isAssignTarget && filenameMatchesTransaction(activeFilenameForMatching, tx)
              const isAssigning = assigningToTxId === tx.id

              const cardBackground = isAssigning
                ? 'var(--accent-light)'
                : isLikelyMatch
                  ? 'rgba(232,85,42,0.08)'
                  : tx.status === 'uploaded'
                    ? 'rgba(34,160,107,0.08)'
                    : tx.status === 'lost'
                      ? 'rgba(217,119,6,0.06)'
                      : 'var(--white)'

              const cardBorder = isLikelyMatch
                ? '2px solid var(--accent)'
                : tx.status === 'uploaded'
                  ? '1.5px solid rgba(34,160,107,0.3)'
                  : tx.status === 'lost'
                    ? '1.5px solid rgba(217,119,6,0.3)'
                    : '1.5px solid var(--surface-2)'

              const cardHandlers = isAssignTarget ? {
                onClick: () => handleRowTap(tx.id),
                onDragOver: e => e.preventDefault(),
                onDrop: e => handleRowDrop(e, tx.id),
              } : {}

              return (
                <div
                  key={tx.id}
                  {...cardHandlers}
                  style={{
                    background: cardBackground,
                    border: cardBorder,
                    borderRadius: 'var(--radius-lg)',
                    padding: '14px 16px',
                    cursor: (selectedStagedId && isAssignTarget) ? 'pointer' : 'default',
                    transition: 'all 0.15s',
                  }}
                >
                  {/* Top row: status badge + date */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%',
                        background: tx.status === 'uploaded' ? 'var(--green-light)'
                          : tx.status === 'lost' ? 'var(--amber-light)' : 'var(--surface-2)',
                        color: tx.status === 'uploaded' ? 'var(--green)'
                          : tx.status === 'lost' ? 'var(--amber)' : 'var(--ink-faint)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                      }}>
                        {tx.status === 'uploaded' ? '✓' : tx.status === 'lost' ? '!' : i + 1}
                      </div>
                      <span style={{ color: 'var(--ink-muted)', fontSize: '0.78rem' }}>
                        {formatDate(tx.date)}
                      </span>
                    </div>
                    <span style={{
                      fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 100,
                      background: tx.amount > 0 ? 'var(--green-light)' : 'var(--surface-2)',
                      color: tx.amount > 0 ? 'var(--green)' : 'var(--ink-muted)',
                      whiteSpace: 'nowrap',
                    }}>
                      {tx.amount > 0 ? 'Income' : 'Expenditure'}
                    </span>
                  </div>

                  {/* Description */}
                  <div style={{ fontSize: '0.95rem', fontWeight: 500, marginBottom: 4, lineHeight: 1.35 }}>
                    {tx.description}
                  </div>

                  {/* Amount */}
                  <div style={{
                    fontSize: '1.15rem', fontWeight: 700, marginBottom: 12,
                    color: tx.amount > 0 ? 'var(--green)' : 'var(--ink)',
                  }}>
                    {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount)}
                  </div>

                  {isLikelyMatch && (
                    <div style={{ fontSize: '0.78rem', color: 'var(--accent)', marginBottom: 10, fontWeight: 600 }}>
                      ↳ likely match for the selected file
                    </div>
                  )}

                  {/* Receipt section */}
                  <div style={{
                    paddingTop: 12,
                    borderTop: '1px solid var(--surface-2)',
                    marginBottom: 10,
                  }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                      Receipt
                    </div>
                    <UploadCell transaction={tx} token={token} onRefresh={load} refreshKey={refreshKey} />
                  </div>

                  {/* Note section */}
                  <div style={{
                    paddingTop: 10,
                    borderTop: '1px solid var(--surface-2)',
                  }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                      Note for bookkeeper
                    </div>
                    <NoteCell transaction={tx} token={token} onRefresh={load} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <p style={{ textAlign: 'center', marginTop: 32, fontSize: '0.75rem', color: 'var(--ink-faint)' }}>
          Powered by Receipt Chaser · Your files are securely stored
        </p>
      </main>
    </div>
  )
}
